import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Activity, CircleGauge, Fish, MapPin, Sparkles, Thermometer } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useFishingIntelligence } from "../../features/fishingIntelligence/useFishingIntelligence";
import {
  fishingSettingLocationKey,
  normalizeFishingSetting,
} from "../../features/fishingIntelligence/fishingSetting";
import {
  COMMON_CARP_SPECIES_ID,
  FISHING_INTELLIGENCE_SPECIES,
  GENERAL_OUTLOOK_ID,
} from "../../features/fishingIntelligence/speciesCatalog";
import { isMackerelRegional, normalizeMackerelCapabilities, withMackerelScope, regionalConfidence } from "../../features/fishingIntelligence/mackerelCapabilities";
import SpeciesFishingSetting from "./SpeciesFishingSetting";
import MarineFishingPlatform from "./MarineFishingPlatform";
import { explicitMarinePlatform, marineContextRequired } from "../../features/fishingIntelligence/marineFishingContext";
import FishingIntelligenceExplanations from "./FishingIntelligenceExplanations";
import SpeciesIntelligenceTimeline from "./SpeciesIntelligenceTimeline";
import SpeciesMultiDayForecast from "./SpeciesMultiDayForecast";
import CommonCarpSessionInputs from "./CommonCarpSessionInputs";
import GeneralConditionsSection from "./GeneralConditionsSection";
import "../../styles/components/SpeciesIntelligenceSection.css";

const translatedCode = (t, prefix, value) => {
  return t(`${prefix}_${value || "unknown"}`, {
    defaultValue: t("fishing_today_intelligence_unknown"),
  });
};

const translatedRegion = (t, context) => {
  const region = context.regionId
    ? translatedCode(t, "fishing_today_intelligence_region", context.regionId)
    : t("fishing_today_intelligence_region_unresolved");
  return context.isTransition
    ? `${region} · ${t("fishing_today_intelligence_region_transition")}`
    : region;
};

const ScoreCard = ({ className = "", hint, icon: Icon, label, score, primary = false, unavailableLabel, t }) => {
  const available = score !== null && score !== undefined;
  return (
    <div
      className={`rp-species-intel__score rp-species-intel__score--${
        available ? score.label : "unavailable"
      } ${primary ? "rp-species-intel__score--primary" : ""} ${className}`.trim()}
    >
      <div className="rp-species-intel__score-heading">
        <Icon size={primary ? 21 : 18} aria-hidden="true" />
        <span>{label}</span>
      </div>
      {available ? (
        <>
          <strong className="rp-species-intel__score-value">
            {t("fishing_today_intelligence_score_value", { score: score.score })}
          </strong>
          <span className="rp-species-intel__score-label">
            {t(`fishing_today_intelligence_score_${score.label}`)}
          </span>
        </>
      ) : (
        <strong className="rp-species-intel__score-unavailable">
          {unavailableLabel || t("fishing_today_intelligence_unavailable")}
        </strong>
      )}
      {hint ? <small className="rp-species-intel__score-hint">{hint}</small> : null}
    </div>
  );
};

const Context = ({ context, t }) => (
  <dl className="rp-species-intel__context" aria-label={t("fishing_today_intelligence_context") }>
    <div>
      <dt>{t("fishing_today_intelligence_region")}</dt>
      <dd>{translatedRegion(t, context)}</dd>
    </div>
    <div>
      <dt>{t("fishing_today_intelligence_waterbody")}</dt>
      <dd>{translatedCode(t, "fishing_today_intelligence_waterbody", context.waterbody)}</dd>
    </div>
    <div>
      <dt>{t("fishing_today_intelligence_precision")}</dt>
      <dd>{translatedCode(t, "fishing_today_intelligence_precision", context.locationPrecision)}</dd>
    </div>
  </dl>
);

const IntelligenceState = ({ icon: Icon, title, body, context, t, role = "status" }) => (
  <div className="rp-species-intel__state" role={role}>
    <Icon size={25} aria-hidden="true" />
    <div>
      <strong>{title}</strong>
      <p>{body}</p>
    </div>
    {context ? <Context context={context} t={t} /> : null}
  </div>
);

const SpeciesResults = ({ data, speciesId, error, isCommonCarp, isLoading, locale, onRetry, selection, onSelectionChange, t }) => {
  if (isLoading) {
    return (
      <div
        className="rp-species-intel__loading"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <span className="rp-species-intel__loading-copy">
          {t("fishing_today_intelligence_loading")}
        </span>
        <span /><span /><span />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rp-species-intel__state rp-species-intel__state--error" role="alert">
        <CircleGauge size={25} aria-hidden="true" />
        <div>
          <strong>{t("fishing_today_intelligence_error_title")}</strong>
          <p>{t("fishing_today_intelligence_error_body")}</p>
          <button type="button" onClick={onRetry}>
            {t("fishing_today_retry")}
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  if (
    isCommonCarp &&
    data.context.locationPrecision === "profile_city" &&
    data.context.waterbodyIdentified !== true
  ) {
    return null;
  }

  const wrongWaterbody =
    data.current.eligibility.biological === "blocked" &&
    data.current.eligibility.reasonCodes.includes("waterbody_incompatible");
  if (wrongWaterbody) {
    return (
      <IntelligenceState
        icon={MapPin}
        title={t("fishing_today_intelligence_waterbody_incompatible_title")}
        body={t("fishing_today_intelligence_waterbody_incompatible_body")}
        context={data.context}
        t={t}
      />
    );
  }

  const unresolvedRegion =
    data.context.supportedForSpecies !== true &&
    (!data.context.regionId || data.context.resolutionLevel === "unresolved");
  if (data.context.supportedForSpecies === false || unresolvedRegion) {
    return (
      <IntelligenceState
        icon={MapPin}
        title={t("fishing_today_intelligence_region_unsupported_title")}
        body={t("fishing_today_intelligence_region_unsupported_body")}
        context={data.context}
        t={t}
      />
    );
  }

  const hasUsableBiologicalDimension = [
    data.current.fishingOpportunity,
    data.current.availability,
    data.current.activity,
  ].some((value) => value !== null && value !== undefined);
  if (
    isCommonCarp &&
    data.context.waterbodyIdentified === true &&
    ["found_untrusted", "unavailable"].includes(
      data.context.freshwaterTemperature?.status
    ) &&
    !hasUsableBiologicalDimension
  ) {
    return (
      <IntelligenceState
        icon={Thermometer}
        title={t("fishing_today_carp_coverage_unavailable_title")}
        body={t("fishing_today_carp_coverage_unavailable_body")}
        context={data.context}
        t={t}
      />
    );
  }
  if (!hasUsableBiologicalDimension) {
    return (
      <IntelligenceState
        icon={CircleGauge}
        title={t("fishing_today_intelligence_data_unavailable_title")}
        body={t("fishing_today_intelligence_data_unavailable_body")}
        context={data.context}
        t={t}
      />
    );
  }

  const context = Object.prototype.hasOwnProperty.call(data.context, "capabilities") ? data.context : {
    ...data.context,
    capabilities: normalizeMackerelCapabilities(speciesId),
  };
  const regional = isMackerelRegional(context);
  const confidence = regional ? regionalConfidence(data.current) : data.current.confidence;
  t = withMackerelScope(t, context);
  const missingWaterTemperature = [
    ...data.missingInputs,
    ...data.current.missingInputs,
  ].some((value) =>
    ["water_temperature", "trusted_freshwater_temperature"].includes(value)
  );

  return (
    <div className="rp-species-intel__results" aria-live="polite">
      <ScoreCard
        icon={Sparkles}
        label={t("fishing_today_intelligence_opportunity")}
        score={regional ? data.current.availability : data.current.fishingOpportunity}
        hint={t(data.context.seaBassMultifactor ? "sea_bass_opportunity_semantics" : "fishing_today_intelligence_opportunity_hint")}
        primary
        t={t}
      />
      {regional ? <p className="rp-species-intel__scope-note">{t("mackerel_disclosure")}</p> : null}
      <div className={`rp-species-intel__score-grid${regional ? " rp-species-intel__score-grid--regional" : ""}`}>
        {!regional ? <ScoreCard
          icon={Fish}
          label={t("fishing_today_intelligence_availability")}
          score={data.current.availability}
          hint={data.context.seaBassMultifactor ? t("sea_bass_availability_semantics") : null}
          t={t}
        /> : null}
        <ScoreCard
          icon={Activity}
          label={t("fishing_today_intelligence_activity")}
          score={data.current.activity}
          unavailableLabel={regional ? t("mackerel_not_modelled") : null}
          hint={data.context.seaBassMultifactor ? t("sea_bass_activity_semantics") : null}
          t={t}
        />
        <div className="rp-species-intel__confidence">
          <div className="rp-species-intel__score-heading">
            <CircleGauge size={18} aria-hidden="true" />
            <span>{t("fishing_today_intelligence_confidence")}</span>
          </div>
          {confidence ? (
            <>
              <strong>
                {t("fishing_today_intelligence_score_value", {
                  score: confidence.score,
                })}
              </strong>
              <span>
                {t(`fishing_today_intelligence_confidence_${confidence.level}`)}
              </span>
            </>
          ) : (
            <strong>{t("fishing_today_intelligence_unavailable")}</strong>
          )}
        </div>
      </div>
      <FishingIntelligenceExplanations
        context={context}
        point={data.current}
        t={t}
      />
      {isCommonCarp && !data.context.freshwaterTemperature?.futureSupported ? (
        <IntelligenceState
          icon={CircleGauge}
          title={t("fishing_today_carp_future_unavailable_title")}
          body={t("fishing_today_carp_future_unavailable_body")}
          t={t}
        />
      ) : data.forecastDays?.length ? (
        <SpeciesMultiDayForecast
          context={context}
          currentTimestamp={data.current.timestamp}
          forecastDays={data.forecastDays}
          next24Hours={data.next24Hours}
          selection={selection}
          onSelectionChange={onSelectionChange}
          onRefresh={onRetry}
          forecastTimezone={
            data.forecastMetadata?.timezone || data.context.forecastTimezone
          }
          locale={locale}
          t={t}
        />
      ) : (
        <SpeciesIntelligenceTimeline
          context={context}
          currentTimestamp={data.current.timestamp}
          forecastTimezone={data.context.forecastTimezone}
          locale={locale}
          t={t}
          timeline={data.timeline}
        />
      )}
      {missingWaterTemperature ? (
        <p className="rp-species-intel__missing" role="status">
          {t("fishing_today_intelligence_missing_water_temperature")}
        </p>
      ) : null}
      <Context context={data.context} t={t} />
    </div>
  );
};

export default function SpeciesIntelligenceSection({
  activeLocation,
  generalConditions = null,
  initialFishingSetting = null,
  initialFishingPlatform = null,
  outlookLocationKey = "",
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n?.resolvedLanguage || i18n?.language || "en";
  const [selectedSpeciesId, setSelectedSpeciesId] = useState(GENERAL_OUTLOOK_ID);
  const locationKey = fishingSettingLocationKey(activeLocation, outlookLocationKey);
  const selectionKey = `${selectedSpeciesId}:${locationKey}`;
  const [forecastSelection, setForecastSelection] = useState(null);
  const rememberSelection = useCallback((value) => setForecastSelection((previous) =>
    previous?.contextKey === selectionKey && previous.viewKey === value.viewKey && previous.timestamp === value.timestamp
      ? previous : { ...value, contextKey: selectionKey }), [selectionKey]);
  const savedPlatform = explicitMarinePlatform(initialFishingPlatform);
  const favoriteKey = activeLocation?.source === "favorite_spot" ? `${locationKey}:${savedPlatform}` : null;
  const [platformState, setPlatformState] = useState(() => ({ favoriteKey, value: savedPlatform }));
  // Explicit Favorite context replaces the session choice; legacy Favorites ask.
  // Profile/GPS changes retain the user's choice, never infer it from location.
  const platform = favoriteKey !== null && platformState.favoriteKey !== favoriteKey ? savedPlatform : platformState.value;
  useEffect(() => {
    setPlatformState((current) => current.favoriteKey === favoriteKey ? current
      : { favoriteKey, value: favoriteKey !== null ? savedPlatform : current.value });
  }, [favoriteKey, savedPlatform]);
  const normalizedInitialSetting = useMemo(
    () => normalizeFishingSetting(initialFishingSetting),
    [initialFishingSetting]
  );
  const preloadKey = JSON.stringify(normalizedInitialSetting);
  const [settingState, setSettingState] = useState(() => ({
    locationKey,
    preloadKey,
    value: normalizedInitialSetting,
  }));
  const [carpSessionState, setCarpSessionState] = useState(() => ({
    locationKey,
    submitted: null,
  }));
  const fishingSetting =
    settingState.locationKey === locationKey &&
    settingState.preloadKey === preloadKey
      ? settingState.value
      : normalizedInitialSetting;
  const speciesId =
    selectedSpeciesId === GENERAL_OUTLOOK_ID ? null : selectedSpeciesId;
  const selectedSpecies = FISHING_INTELLIGENCE_SPECIES.find(
    (species) => species.id === speciesId
  );
  const isCommonCarp = speciesId === COMMON_CARP_SPECIES_ID;
  const carpSessionEvidence =
    isCommonCarp && carpSessionState.locationKey === locationKey
      ? carpSessionState.submitted
      : null;
  const intelligence = useFishingIntelligence(
    speciesId,
    activeLocation,
    outlookLocationKey,
    fishingSetting,
    carpSessionEvidence,
    marineContextRequired(speciesId) ? platform : null
  );

  useEffect(() => {
    setSettingState((current) =>
      current.locationKey === locationKey && current.preloadKey === preloadKey
        ? current
        : {
            locationKey,
            preloadKey,
            value: normalizedInitialSetting,
          }
    );
  }, [locationKey, normalizedInitialSetting, preloadKey]);

  useEffect(() => {
    setCarpSessionState((current) =>
      current.locationKey === locationKey
        ? current
        : { locationKey, submitted: null }
    );
  }, [locationKey]);

  const updateFishingSetting = (value) => {
    setSettingState({
      locationKey,
      preloadKey,
      value: normalizeFishingSetting(value),
    });
  };

  const updateCarpSessionEvidence = (submitted) => {
    setCarpSessionState({ locationKey, submitted });
  };

  const clearCarpSessionEvidence = () => {
    setCarpSessionState({ locationKey, submitted: null });
  };

  return (
    <section className="rp-species-intel" aria-labelledby="fishing-intelligence-title">
      <div className="rp-species-intel__selector-row">
        <div>
          <h3 id="fishing-intelligence-title">
            {t("fishing_today_intelligence_title")}
          </h3>
          <p>{t("fishing_today_intelligence_intro")}</p>
        </div>
        <label>
          <span>{t("fishing_today_intelligence_species_label")}</span>
          <select
            value={selectedSpeciesId}
            onChange={(event) => setSelectedSpeciesId(event.target.value)}
          >
            <option value={GENERAL_OUTLOOK_ID}>
              {t("fishing_today_intelligence_general")}
            </option>
            {FISHING_INTELLIGENCE_SPECIES.map((species) => (
              <option key={species.id} value={species.id}>
                {t(species.translationKey)}
              </option>
            ))}
          </select>
        </label>
      </div>
      {speciesId ? (
        <>
          <p className="rp-species-intel__identity">
            <strong>{t(selectedSpecies.translationKey)}</strong>
            <em>{selectedSpecies.scientificName}</em>
          </p>
          {isCommonCarp ? (
            <CommonCarpSessionInputs
              context={intelligence.data?.context || null}
              isLoading={intelligence.isLoading}
              locationKey={locationKey}
              onClear={clearCarpSessionEvidence}
              onSubmit={updateCarpSessionEvidence}
              submitted={carpSessionEvidence}
              t={t}
            />
          ) : null}
          {marineContextRequired(speciesId) ? <MarineFishingPlatform
            value={platform} onChange={(value) => setPlatformState({ favoriteKey, value })} t={t} /> : null}
          <SpeciesFishingSetting
            platform={marineContextRequired(speciesId) ? platform : null}
            declaredHabitat={intelligence.data?.context?.declaredHabitat}
            isLoading={intelligence.isLoading}
            onChange={updateFishingSetting}
            t={t}
            value={fishingSetting}
            waterbody={intelligence.data?.context?.waterbody || "unknown"}
          />
          <SpeciesResults
            {...intelligence}
            speciesId={speciesId}
            selection={forecastSelection?.contextKey === selectionKey ? forecastSelection : null}
            onSelectionChange={rememberSelection}
            isCommonCarp={isCommonCarp}
            locale={locale}
            onRetry={intelligence.retry}
            t={t}
          />
        </>
      ) : (
        <GeneralConditionsSection
          data={generalConditions}
          locale={locale}
          t={t}
        />
      )}
    </section>
  );
}
